import { SearchOutlined } from '@ant-design/icons';
import { Button, DatePicker, Input, Space, Spin } from 'antd';
import type { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { workflowsApi } from '@/api/workflows';
import { AuditLog } from '@/approver/AuditLog';
import { useRoles } from '@/auth/useRoles';
import { PageHeader } from '@/components/PageHeader';
import { useFetchedList } from '@/hooks/useFetchedList';
import { CARD, CARD_BODY, OUTER_WRAP } from '@/styles/workflowCardStyles';

const { RangePicker } = DatePicker;

/** Matches the Canada Post autocomplete's cadence — long enough to avoid a
 *  request per keystroke, short enough to feel live. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * Dedicated Audit History page — mounts the `<AuditLog>` table under a
 * full-page shell (not a dashboard tab). The header dropdown's "Audit
 * history" menu item routes here; the BE `/workflows/audit` endpoint
 * scopes results by role automatically (Approver → firm-wide feed;
 * Originator → their own workflows only). Unlike the old Recent Decisions
 * view this shows EVERY state change, not just approved/rejected versions.
 *
 * Search + date range are sent to the SERVER, not applied to the rows already
 * on screen. The endpoint caps the feed at the newest N events, so filtering
 * here would only ever search inside that window — a date range covering last
 * month would come back empty whenever the newest N rows are all recent.
 */
export function AuditHistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role } = useRoles();

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  // Serialize to ISO date strings for the request. Only whole days are sent —
  // the BE widens the upper bound to end-of-day so a single-day range selects
  // that entire day rather than the instant of midnight.
  const from = range?.[0]?.startOf('day').toISOString();
  const to = range?.[1]?.startOf('day').toISOString();

  const { data: events, loading } = useFetchedList(
    () => workflowsApi.auditLog({ q: debouncedQuery || undefined, from, to }),
    t,
    'approver.recent.loadFailed',
    [debouncedQuery, from, to],
  );

  const dashboardRoute = role === 'Approver' ? '/approver/dashboard' : '/originator/dashboard';

  const filtering = useMemo(
    () => Boolean(debouncedQuery) || Boolean(from) || Boolean(to),
    [debouncedQuery, from, to],
  );

  const clearAll = () => {
    setQuery('');
    setDebouncedQuery('');
    setRange(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PageHeader
        title={t('auth.auditHistory', { defaultValue: 'Audit history' })}
        onBack={() => navigate(dashboardRoute)}
      />
      <div style={OUTER_WRAP}>
        <section style={CARD}>
          <div style={CARD_BODY}>
            {/* Filter bar. `wrap` so the controls stack on a narrow viewport
                instead of overflowing the card. */}
            <Space wrap style={{ marginBottom: 16 }} size={8}>
              <Input
                allowClear
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('audit.searchPlaceholder', {
                  defaultValue: 'Search by name or email',
                })}
                prefix={<SearchOutlined style={{ color: '#6b7280' }} />}
                // No pill radius here — this sits beside the RangePicker, and the
                // two controls read as one filter bar only if their corners match.
                // (The dashboard search boxes ARE pills; they stand alone there.)
                style={{ width: 280 }}
              />
              <RangePicker
                value={range}
                onChange={(v) => setRange(v as [Dayjs | null, Dayjs | null] | null)}
                allowEmpty={[true, true]}
                format="DD/MM/YYYY"
              />
              {filtering && (
                <Button onClick={clearAll}>
                  {t('workflow.list.clearSearch', { defaultValue: 'Clear' })}
                </Button>
              )}
            </Space>
            {loading && !events ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                <Spin />
              </div>
            ) : (
              <AuditLog items={events} loading={loading} filtering={filtering} />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
